import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../../components/feature/SiteHeader';
import SiteFooter from '../../components/feature/SiteFooter';
import ArticleCard from '../../components/feature/ArticleCard';
import SectionHeading from '../../components/feature/SectionHeading';
import HeroCarousel from './components/HeroCarousel';
import {
  formatViews,
  getCategories,
  getFeaturedArticles,
  getHotArticles,
  getHotTopics,
  getLatestArticles,
  getSpecials,
} from '../../lib/content';

export default function Home() {
  const featured = getFeaturedArticles();
  const latest = getLatestArticles();
  const hot = getHotArticles(8);
  const categories = getCategories();
  const topics = getHotTopics();
  const specials = getSpecials();
  const [visibleCount, setVisibleCount] = useState(6);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <HeroCarousel articles={featured} />

        {/* 分类快捷入口 */}
        <section className="w-full px-4 md:px-8 -mt-10 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                className="group flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <span className="shrink-0 w-11 h-11 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center text-xl group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={cat.icon} />
                </span>
                <span className="min-w-0">
                  <span className="block font-heading font-semibold text-sm md:text-base text-foreground-950">
                    {cat.name}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500 line-clamp-2">
                    {cat.desc}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* 主体：左内容 + 右侧栏 */}
        <section className="w-full px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* 最新内容 */}
            <div className="lg:col-span-8">
              <SectionHeading
                title="最新内容"
                icon="ri-flashlight-line"
                description="按发布时间排序的最新资讯与热点"
                action={
                  <Link
                    to="/category/news"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-0.5 whitespace-nowrap"
                  >
                    更多
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                }
              />

              <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                {latest.slice(0, visibleCount).map((article) => (
                  <ArticleCard key={article.id} article={article} variant="list" />
                ))}
              </div>

              {visibleCount < latest.length && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + 6)}
                    className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" />
                    加载更多
                  </button>
                </div>
              )}
            </div>

            {/* 右侧栏 */}
            <aside className="lg:col-span-4 space-y-8">
              {/* 热门话题 */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <SectionHeading title="热门话题" icon="ri-fire-line" />
                <ul>
                  {topics.map((topic, index) => (
                    <li key={topic.id}>
                      <Link
                        to={topic.target}
                        className="group flex items-center gap-3 py-2.5 border-b border-background-200 last:border-0"
                      >
                        <span
                          className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-heading font-bold ${
                            index < 3
                              ? 'bg-accent-500 text-white'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="flex-1 min-w-0 text-sm text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                          {topic.label}
                        </span>
                        <span className="text-xs text-foreground-400 flex items-center gap-0.5">
                          <i className="ri-fire-line" />
                          {formatViews(topic.heat)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 推荐专题 */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <SectionHeading
                  title="推荐专题"
                  icon="ri-price-tag-3-line"
                  action={
                    <Link
                      to="/special"
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-0.5 whitespace-nowrap"
                    >
                      全部
                      <i className="ri-arrow-right-s-line" />
                    </Link>
                  }
                />
                <div className="space-y-3">
                  {specials.map((sp) => (
                    <Link
                      key={sp.id}
                      to={`/special?id=${sp.id}`}
                      className="group flex gap-3 items-center"
                    >
                      <div className="shrink-0 w-16 h-12 rounded-md overflow-hidden bg-background-100">
                        <img
                          src={sp.cover}
                          alt={sp.title}
                          title={`${sp.title} - WWW.17C.COM`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                          {sp.title}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-500">
                          {sp.tag}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* 本周热榜 */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <SectionHeading title="本周热榜" icon="ri-bar-chart-2-line" />
                <div>
                  {hot.slice(0, 6).map((article, index) => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      variant="compact"
                      rank={index + 1}
                    />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* 专题推荐横排 */}
        <section className="w-full px-4 md:px-8 pb-4">
          <SectionHeading title="精选专题策划" icon="ri-layout-masonry-line" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {specials.map((sp) => (
              <Link
                key={sp.id}
                to={`/special?id=${sp.id}`}
                className="group relative rounded-lg overflow-hidden h-44"
              >
                <img
                  src={sp.cover}
                  alt={sp.title}
                  title={`${sp.title} - WWW.17C.COM`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block text-xs text-white bg-accent-500 px-2 py-0.5 rounded-md mb-2">
                    {sp.tag}
                  </span>
                  <h3 className="font-heading font-semibold text-base text-white leading-snug">
                    {sp.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* 网站简介 */}
        <section className="w-full px-4 md:px-8 py-10 mt-6">
          <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-primary-900">
                  关于 WWW.17C.COM
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  WWW.17C.COM 是一个专注多元内容的综合门户平台，聚合新闻资讯、娱乐八卦、科技动态、生活服务与专题策划，力求为每一位用户提供方便、高效且版面清晰的信息获取体验。我们坚持内容分层清晰、浏览轻快，让你需要的每一条信息都触手可及。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    了解我们
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <Link
                    to="/special"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-medium border border-primary-200 hover:bg-primary-100 transition-colors whitespace-nowrap"
                  >
                    阅读专题
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-background-50 p-4">
                  <div className="font-heading font-bold text-2xl text-primary-600">4</div>
                  <div className="mt-1 text-xs text-foreground-600">内容分类</div>
                </div>
                <div className="rounded-lg bg-background-50 p-4">
                  <div className="font-heading font-bold text-2xl text-accent-500">4</div>
                  <div className="mt-1 text-xs text-foreground-600">精选专题</div>
                </div>
                <div className="rounded-lg bg-background-50 p-4">
                  <div className="font-heading font-bold text-2xl text-primary-600">24+</div>
                  <div className="mt-1 text-xs text-foreground-600">演示内容</div>
                </div>
                <div className="rounded-lg bg-background-50 p-4">
                  <div className="font-heading font-bold text-2xl text-accent-500">7×24</div>
                  <div className="mt-1 text-xs text-foreground-600">持续更新</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}